
import { Link } from "react-router-dom";
import { Atom, Microscope, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/shared/Header";

const NaturalSciences = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 px-6 bg-whiter from-primary/5 to-muted/20">
        <div className="container mx-auto text-center max-w-4xl">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-8">
            <Atom className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-5xl font-bold mb-6 bg-white from-primary to-primary/70 bg-clip-text text-transparent">
            Natural & Physical Sciences
          </h1>
          <p className="text-xl text-black leading-relaxed mb-8">
            Humanity's unfolding attempt to understand the observable world—from the smallest particles 
            to vast ecosystems, from fundamental forces to complex organisms.
          </p>
          <ArrowDown className="mx-auto w-6 h-6 text-black animate-bounce" />
        </div>
      </section>

      {/* Main Domains */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid gap-8">
            
            {/* Physical Sciences */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Atom className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Physical Sciences</CardTitle>
                    <CardDescription className="text-lg">
                      The study of non-living systems and the fundamental laws governing matter, motion, energy, and spacetime
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Core Disciplines</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Physics: From quantum mechanics to cosmology</li>
                      <li>• Chemistry: Atomic structure to complex reactions</li>
                      <li>• Earth Sciences: Geology, meteorology, oceanography</li>
                      <li>• Astronomy: Celestial mechanics to space exploration</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Key Areas</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Fundamental forces and particles</li>
                      <li>• Chemical bonding and molecular behavior</li>
                      <li>• Planetary systems and climate</li>
                      <li>• Stellar evolution and cosmic structures</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/natural-sciences/physical-sciences">Explore Physical Sciences</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Natural Sciences */}
            <Card className="group hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <Microscope className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Natural Sciences</CardTitle>
                    <CardDescription className="text-lg">
                      The exploration of living systems, ecological relationships, and the structures and behaviors of life
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Life Sciences</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Biology: From molecules to ecosystems</li>
                      <li>• Ecology: Environmental interactions</li>
                      <li>• Botany & Zoology: Plant and animal studies</li>
                      <li>• Microbiology: Microscopic life forms</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Research Focus</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Evolutionary processes and genetics</li>
                      <li>• Biodiversity and conservation</li>
                      <li>• Ecosystem dynamics and sustainability</li>
                      <li>• Disease mechanisms and immunity</li>
                    </ul>
                  </div>
                </div>
                <Button asChild>
                  <Link to="/natural-sciences/life-sciences">Explore Natural Sciences</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Interdisciplinary Connections */}
            <Card className="group hover:shadow-lg transition-all duration-300 border-2 border-primary/20">
              <CardHeader>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <div className="w-6 h-6 rounded-full bg-primary/50 flex items-center justify-center">
                      <div className="w-3 h-3 rounded-full bg-primary"></div>
                    </div>
                  </div>
                  <div>
                    <CardTitle className="text-2xl">Interdisciplinary Connections</CardTitle>
                    <CardDescription className="text-lg">
                      Fields at the intersection of physical and natural sciences, bridging domains of inquiry
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Cross-Disciplinary Fields</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Biophysics: Physical principles in biology</li>
                      <li>• Geochemistry: Chemical-geological interactions</li>
                      <li>• Astrobiology: Life beyond Earth</li>
                      <li>• Environmental Chemistry: Chemical ecology</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Emerging Areas</h4>
                    <ul className="space-y-2 text-black">
                      <li>• Mathematical Biology: Quantitative modeling</li>
                      <li>• Neuroscience: Brain and behavior</li>
                      <li>• Climate Science: Earth system dynamics</li>
                      <li>• Computational Biology: Data-driven research</li>
                    </ul>
                  </div>
                </div>
                <Button asChild variant="outline">
                  <Link to="/natural-sciences/interdisciplinary">Explore Connections</Link>
                </Button>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 px-6 bg-muted/30">
        <div className="container mx-auto text-center max-w-4xl">
          <h3 className="text-2xl font-bold mb-6">Continue Exploring</h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="outline">
              <Link to="/world-itself">Back to The World Itself</Link>
            </Button>
            <Button asChild size="lg">
              <Link to="/engineering">Engineering & Technology</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NaturalSciences;

